import { ColorFresnelModel } from './color-fresnel.model';
import { ScalarFresnelModel } from './scalar-fresnel.model';
import {
  ColorFresnelValue,
  ScalarFresnelValue,
  EngineColorValue
} from './recipe-types';
import { EngineColorRecipeModel } from './engine-color-recipe.model';

function getScalarFresnelVal(
  recipe: EngineColorRecipeModel,
  prop: 'saturation' | 'brightness' | 'opacity' | 'contrast'
): ScalarFresnelValue | false | undefined {
  switch (true) {
    case recipe[prop] === undefined:
      return undefined;
    case recipe[prop] === false:
      return false;
    case typeof recipe[prop] === 'number':
      return (recipe[prop] as number) * 100;
    default:
      return new ScalarFresnelModel({
        center: recipe[prop][0],
        edge: recipe[prop][1],
        centerBias: recipe[prop][2],
        edgeBias: recipe[prop][3],
        power: recipe[prop][4]
      });
  }
}

// application color recipe
export class ColorRecipeModel {
  // false = remove color mode, undefined = don't touch color mod
  tintColor?: ColorFresnelValue | false | undefined;
  saturation?: ScalarFresnelValue | false | undefined;
  contrast?: ScalarFresnelValue | false | undefined;
  brightness?: ScalarFresnelValue | false | undefined;
  opacity?: ScalarFresnelValue | false | undefined;

  constructor(value: ColorRecipeModel = {}) {
    const { tintColor, saturation, contrast, brightness, opacity } = value;
    this.tintColor = tintColor;
    this.saturation = saturation;
    this.contrast = contrast;
    this.brightness = brightness;
    this.opacity = opacity;
  }

  static isRealRecipe(recipe: ColorRecipeModel) {
    return recipe.brightness !== false ||
      recipe.contrast !== false ||
      recipe.opacity !== false ||
      recipe.saturation !== false ||
      recipe.tintColor !== false;
  }

  static fromEngine(recipe: EngineColorRecipeModel): ColorRecipeModel {
    const tintColor = (() => {
      switch (true) {
        case recipe.tintColor === undefined:
          return undefined;
        case recipe.tintColor === false:
          return false;
        case Array.isArray(recipe.tintColor): {
          // at this point, we know tintColor is an array, so it's safe to cast.
          return (recipe.tintColor as number[]).length === 3
            ? [...(recipe.tintColor as number[])]
            : new ColorFresnelModel({
                center: recipe.tintColor[0],
                edge: recipe.tintColor[1],
                centerBias: recipe.tintColor[2],
                edgeBias: recipe.tintColor[3],
                power: recipe.tintColor[4]
              });
        }
      }
    })();
    return new ColorRecipeModel({
      tintColor: tintColor as ColorFresnelValue | false | undefined,
      saturation: getScalarFresnelVal(recipe, 'saturation'),
      brightness: getScalarFresnelVal(recipe, 'brightness'),
      contrast: getScalarFresnelVal(recipe, 'contrast'),
      opacity: getScalarFresnelVal(recipe, 'opacity'),
    });
  }

  /**
   *  Converts an Application Recipe to an Engine Recipe
   */
  static toEngine(recipe: ColorRecipeModel): EngineColorRecipeModel {
    // initialize values: false -> explicitly remove color setting
    let tintColor: EngineColorValue[] | false =
      recipe.tintColor === false ? false : undefined;
    let saturation: EngineColorValue | false =
      recipe.saturation === false ? false : undefined;
    let brightness: EngineColorValue | false =
      recipe.brightness === false ? false : undefined;
    let contrast: EngineColorValue | false =
      recipe.contrast === false ? false : undefined;
    let opacity: EngineColorValue | false =
      recipe.opacity === false ? false : undefined;

    // tint-color
    if (Array.isArray(recipe.tintColor)) {
      tintColor = [...recipe.tintColor];
    } else if (
      recipe.tintColor &&
      recipe.tintColor instanceof ColorFresnelModel
    ) {
      tintColor = ColorFresnelModel.toEngine(recipe.tintColor);
    }

    // saturation
    if (typeof recipe.saturation === 'number') {
      saturation = recipe.saturation / 100.0;
    } else if (recipe.saturation instanceof ScalarFresnelModel) {
      saturation = ScalarFresnelModel.toEngine(recipe.saturation);
    }

    // brightness
    if (typeof recipe.brightness === 'number') {
      brightness = recipe.brightness / 100.0;
    } else if (recipe.brightness instanceof ScalarFresnelModel) {
      brightness = ScalarFresnelModel.toEngine(recipe.brightness);
    }

    // contrast
    if (typeof recipe.contrast === 'number') {
      contrast = recipe.contrast / 100.0;
    } else if (recipe.contrast instanceof ScalarFresnelModel) {
      contrast = ScalarFresnelModel.toEngine(recipe.contrast);
    }

    // opacity
    if (typeof recipe.opacity === 'number') {
      opacity = recipe.opacity / 100.0;
    } else if (recipe.opacity instanceof ScalarFresnelModel) {
      opacity = ScalarFresnelModel.toEngine(recipe.opacity);
    }

    return {
      tintColor,
      saturation,
      brightness,
      contrast,
      opacity
    };
  }

  static mergeRecipes(currentRecipe: ColorRecipeModel, actionRecipe: ColorRecipeModel): ColorRecipeModel {
    const newRecipe = Object.assign({}, currentRecipe);

    // Do not overwrite current recipe value if new value is undefined
    for (const [key, value] of Object.entries(actionRecipe)) {
      if (typeof value !== 'undefined') {
        newRecipe[key] = value;
      }
    }

    return newRecipe;
  }
}
